<template>
  <div class="viewer-wrapper">
    <div class="viewer-container">
      <Loading
        :active.sync="isLoading"
        :is-full-page="false"
        :color="loaderColor"
        loader="dots"
        :background-color="loaderBackgroundColor"
      />
      <div class="viewer-header">
        <div class="document-title" :title="documentTitle">
          {{ truncatedTitle }}
        </div>

        <div class="viewer-tools">
          <!-- PAGINATION -->
          <VasionPager
            :currentPageNumber="currentPageNumber"
            :showPageSize="false"
            :totalPages="totalPages"
            @pageChanged="goToPage"
          />
          <!-- ZOOM IN, ZOOM OUT -->
          <div id="toolbar-zoom" class="toolbar-height">
            <div class="toolbar-divider" />
            <VasionButton
              id="btnZoomIn"
              class="toolbar-icon"
              name="btnZoomIn"
              title="Zoom In"
              icon="VasionZoomInIcon"
              @vasionButtonClicked="zoomIn"
            />
            <VasionButton
              id="btnZoomOut"
              class="toolbar-icon"
              name="btnZoomOut"
              title="Zoom Out"
              icon="VasionZoomOutIcon"
              @vasionButtonClicked="zoomOut"
            />
          </div>
          <VasionButton
            v-if="canPrintDocument && !isNativeDocument"
            id="btnPrint"
            class="toolbar-icon"
            name="btnPrint"
            title="Print"
            icon="VasionPrintIcon"
            @vasionButtonClicked="handlePrintClick"
          />
          <VasionButton
            id="btnDownload"
            class="toolbar-icon"
            name="btnDownload"
            title="Download"
            icon="VasionDocumentDownloadIcon"
            @vasionButtonClicked="handleDownloadClick"
          />
        </div>
      </div>

      <div class="viewer-body">
        <div class="viewer-content" id="viewerContent">
          <DocumentImage
            v-if="!isNativeDocument"
            ref="mainImage"
            :documentID="documentId"
            :fileDetail="document"
            :filePath="filePath"
            :height="viewerHeight"
            imageEndpoint="document/getAttachmentDocumentImageURL"
            :pageNumber="currentPageNumber"
            transformOrigin="top left"
            :width="viewerWidth"
            :zoomScale="zoomValue"
            @backgroundImageLoaded="setZoom"
          />
          <div v-else-if="canDownloadDocument">
            <div> Click here to download this file. </div>
            <VasionButton
              id="download-native-button"
              classProp="primary"
              @vasionButtonClicked="handleDownloadClick"
            >
              Download
            </VasionButton>
          </div>
        </div>
      </div>
    </div>
    <VasionSnackbar
      id="snackbar-attachment-docviewer"
      ref="attachmentDocviewerSnackbar"
      :showSnackbarBool.sync="snackbar.showSnackbar"
      :snackbarImage="snackbar.snackbarImage"
      :snackbarSubTitle="snackbar.snackbarSubTitle"
      :snackbarTitle="snackbar.snackbarTitle"
    />
  </div>
</template>

<script>
import DocumentImage from '@/components/document/DocumentImage.vue'
import Loading from 'vue-loading-overlay'
import { loaderBackgroundColor, loaderColor } from '@/assets/js/styleConfig'
import {
  removeIllegalChars,
  testDownloadHasFileExtension,
} from '@/store/helperModules/common.module'

export default {
  name: 'MinimalDocumentViewer',
  components: {
    DocumentImage,
    Loading,
  },
  data() {
    return {
      attachmentName: this.$route.params.attachmentName,
      currentPageNumber: 1,
      document: null,
      documentId: Number(this.$route.params.documentId),
      filePath: this.$route.params.attachmentName || '',
      firstTimeImageLoad: true,
      isLoading: false,
      loaderBackgroundColor,
      loaderColor,
      snackbar: {
        showSnackbar: false,
        snackbarImage: false,
        snackbarSubTitle: '',
        snackbarTitle: '',
      },
      viewerHeight: 2112,
      viewerWidth: 1632,
      zoomDisplay: 100,
      zoomValue: 1,
    }
  },
  computed: {
    canDownloadDocument() {
      return (
        (!this.document?.readOnlyFolderInfo || this.document?.readOnlyFolderInfo.CanExport) &&
        this.$store.getters['common/canDownloadDocument']
      )
    },
    canPrintDocument() {
      return (
        (!this.document?.readOnlyFolderInfo || this.document?.readOnlyFolderInfo.CanPrint) &&
        this.$store.getters['common/canPrintDocument']
      )
    },
    disableZoomIn() {
      return this.zoomValue > 3
    },
    disableZoomOut() {
      return this.zoomValue <= 0.1
    },
    documentTitle() {
      return this.document?.DocumentName || 'Document'
    },
    isNativeDocument() {
      return !this.document || !this.document.FileDetail || this.document.FileDetail.Type !== 0
    },
    totalPages() {
      return this.document?.FileDetail?.PageCount || 1
    },
    truncatedTitle() {
      return this.documentTitle.length > 60
        ? this.documentTitle.slice(0, 57) + '...'
        : this.documentTitle
    },
  },
  async created() {
    await this.loadDocumentImage()
  },
  methods: {
    async downloadAttachmentFile(documentId, attachmentName) {
      let hasError = false
      if (!documentId || typeof documentId !== 'number' || documentId < 0) hasError = true
      if (!attachmentName || attachmentName === '') hasError = true

      if (hasError) {
        this.showSnackbar('Download Error', 'Invalid document or attachment name.', false)
        return
      }

      const downloadPayload = {
        DocumentID: documentId,
        FileName: attachmentName,
      }

      try {
        const downloadResult = await this.$store.dispatch(
          'vault/downloadAttachment',
          downloadPayload
        )
        if (downloadResult) {
          const newFileName = await removeIllegalChars(attachmentName)
          const fileExtension =
            attachmentName.lastIndexOf('.') !== -1
              ? attachmentName.substring(attachmentName.lastIndexOf('.'))
              : ''

          testDownloadHasFileExtension(downloadResult, newFileName, fileExtension)

          this.showSnackbar('Download Successful', `Attachment ${attachmentName} downloaded.`, true)
        } else {
          this.showSnackbar('Download Error', 'Failed to download the attachment.', false)
        }
      } catch (error) {
        this.showSnackbar(
          'Download Error',
          'An error occurred while downloading the attachment.',
          false
        )
      }
    },
    goToPage(pageNumber) {
      setTimeout(() => {
        if (typeof pageNumber !== 'undefined' && pageNumber > 0 && pageNumber <= this.totalPages) {
          this.currentPageNumber = pageNumber
          this.$store.dispatch('document/uploadedPagesPageNumber', this.currentPageNumber)
        }
      }, 200)
    },
    handleDownloadClick() {
      if (!this.canDownloadDocument) return

      const documentId = this.documentId
      const attachmentFileName = this.attachmentName
      this.downloadAttachmentFile(documentId, attachmentFileName)
    },
    async handlePrintClick() {
      if (!this.canPrintDocument) return

      const documentId = this.documentId
      const attachmentFileName = this.attachmentName
      await this.printAttachmentFile(documentId, attachmentFileName)
    },
    async printAttachmentFile(documentId, attachmentName) {
      let hasError = false
      if (!documentId || typeof documentId !== 'number' || documentId < 0) hasError = true
      if (!attachmentName || attachmentName === '') hasError = true

      if (hasError) {
        this.showSnackbar('Print Error', 'Invalid document or attachment name.', false)
        return
      }

      const printPayload = {
        DocumentID: documentId,
        FileName: attachmentName,
      }

      try {
        const printResult = await this.$store.dispatch(
          'vault/downloadAttachmentForPrint',
          printPayload
        )
        
        if (printResult) {
          // eslint-disable-next-line
          const printJS = require('print-js')
          
          if (!printResult.Value) {
            this.showSnackbar('Print Error', 'The file could not be printed.', false)
          } else if (printResult) {
            printJS({
              printable: printResult.Value, 
              type: 'pdf', 
              showModal: true,
              onPrintDialogClose: () => { 
                // Clean up temporary file
                this.$store.dispatch('workflow/deletePrintedFile', printResult)
                
                // Add amplitude tracking if available
                if (this.$amplitude) {
                  this.$amplitude.trackEvent('Document Shared', {
                    'shareType': 'Print',
                    'app': this.currentApp || 'MinimalViewer'
                  })
                }
              },
              onError: () => { 
                this.showSnackbar('Print Error', 'Error printing file. Please check your browser PDF settings.', false)
              },
            });
            
            this.showSnackbar('Print Successful', `Attachment ${attachmentName} sent to printer.`, true)
          }
        } else {
          this.showSnackbar('Print Error', 'Failed to prepare the attachment for printing.', false)
        }
      } catch (error) {
        this.showSnackbar(
          'Print Error',
          'An error occurred while preparing the attachment for printing.',
          false
        )
      }
    },
    async loadDocumentImage() {
      this.isLoading = true
      try {
        this.document = await this.$store.dispatch('document/getDocumentAttachment', {
          DocumentId: this.documentId,
          AttachmentName: this.attachmentName,
        })

        document.title = this.document.DisplayName
      } catch (error) {
        this.showSnackbar('Error', 'Failed to load document.', false)
      } finally {
        this.isLoading = false
      }
    },
    async setZoom(imageWidth) {
      const previousZoomValue = this.zoomValue
      await this.$store.dispatch('document/resetZoomValue')

      if (this.firstTimeImageLoad) {
        this.firstTimeImageLoad = false

        const viewerContentWidth = document.getElementById('viewerContent')?.offsetWidth || 0
        const VIEWER_PADDING_AND_BORDER = 105
        const newZoomValue = viewerContentWidth / (imageWidth + VIEWER_PADDING_AND_BORDER)

        await this.$store.dispatch('document/setZoomValue', newZoomValue)
      } else {
        await this.$store.dispatch('document/setZoomValue', previousZoomValue)
      }
    },
    showSnackbar(title, subTitle, isSuccess) {
      this.snackbar = {
        snackbarTitle: title,
        snackbarSubTitle: subTitle,
        snackbarImage: isSuccess,
        showSnackbar: true,
      }
    },
    zoomIn() {
      if (!this.disableZoomIn) {
        this.$store.dispatch('document/setZoomValue', true)
      }
    },
    zoomOut() {
      if (!this.disableZoomOut) {
        this.$store.dispatch('document/setZoomValue', false)
      }
    },
  },
}
</script>

<style scoped>
.viewer-wrapper {
  background-color: #f5f4f7;
  width: 100vw;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 24px;
  box-sizing: border-box;
}

.viewer-container {
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 1000px;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.viewer-header {
  padding: 16px 24px 8px 24px;
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid #eee;
}

.document-title {
  font-family: 'Archivo Bold', sans-serif;
  font-size: 24px;
  font-weight: bold;
  font-style: normal;
  color: #707273;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 30px;
}

.viewer-tools {
  margin-top: 12px;
  display: flex;
  gap: 12px;
}

.tool-btn {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  padding: 6px;
  transition: background 0.2s ease;
  border-radius: 6px;
}

.tool-btn:hover {
  background-color: #f0f0f0;
}

.viewer-body {
  background-color: #e4e5e6;
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: auto;
}

.viewer-content {
  background-color: #e4e5e6;
  padding: 32px;
  flex: 1;
}

.pdf-mock {
  background: white;
  padding: 40px;
  border-radius: 8px;
  max-width: 800px;
  margin: 0 auto;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.pdf-mock h2 {
  font-size: 20px;
  margin-bottom: 16px;
  color: #2d3748;
}

.pdf-mock p {
  font-size: 14px;
  line-height: 1.6;
  margin-bottom: 12px;
  color: #2d3748;
}

.pdf-mock ul {
  padding-left: 20px;
  margin-bottom: 16px;
}

.pdf-mock li {
  font-size: 14px;
  margin-bottom: 6px;
}
</style>
